<template>
  <div>
    <card>
      <tab-rail class="mb-3" :tabs="tabs" :value="0">
        <template #actions>
          <base-button class="d-none d-sm-inline-block" type="primary" size="sm" @click="createForm">
            <i class="tim-icons icon-simple-add mr-1"></i> Create form
          </base-button>
          <base-button class="d-sm-none" type="primary" size="sm" icon title="Create form" @click="createForm">
            <i class="tim-icons icon-simple-add"></i>
          </base-button>
        </template>
      </tab-rail>

      <filter-bar
        :filters="filterSchema"
        :initial-value="filterValues"
        :loading="refreshing"
        :result-count="rows.length"
        saved-filters-key="request-forms"
        @change="onFiltersChanged"
      />

      <data-table
        class="mt-3"
        :columns="columns"
        :rows="rows"
        row-key="id"
        :loading="refreshing"
        clickable
        @row-click="editForm"
      >
        <template #cell(name)="{ row }">
          <span class="data-table__primary">{{ row.name }}</span>
        </template>
        <template #cell(link)="{ row }">
          <a class="text-white" target="_blank" :href="publicUrl(row)" @click.stop>{{ publicUrl(row) }}</a>
          <base-button icon class="btn-link text-white" title="Copy link" @click.stop="copyLink(row)">
            <i class="tim-icons icon-single-copy-04"></i>
          </base-button>
        </template>
        <template #cell(events_count)="{ row }">
          <span v-if="Number(row.events_count) > 0">{{ row.events_count }}</span>
          <span v-else class="data-table__muted">All events</span>
        </template>
        <template #cell(status)="{ row }">
          <span class="tag-chip text-white" :style="tagChipStyle(statusOf(row).color)">{{ statusOf(row).label }}</span>
        </template>
        <template #actions="{ row }">
          <el-dropdown>
            <base-button simple type="primary" link size="sm">
              <i class="tim-icons el-icon-more"></i>
            </base-button>
            <template #dropdown>
              <el-dropdown-menu>
                <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="editForm(row)">
                  <i class="tim-icons text-default icon-pencil mr-2"></i> Edit form
                </a>
                <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="duplicateForm(row)">
                  <i class="tim-icons text-default icon-single-copy-04 mr-2"></i> Duplicate
                </a>
                <hr>
                <a v-if="Number(row.archived) === 1" class="dropdown-item" href="#" @click.prevent="toggleArchivePrompt(row)">
                  <span class="active-dot mr-2" style="padding: 0; margin-left: -3px; margin-bottom: -3px"></span> Restore
                </a>
                <a v-else class="dropdown-item" href="#" @click.prevent="toggleArchivePrompt(row)">
                  <span class="inactive-dot mr-2" style="padding: 0; margin-left: -3px; margin-bottom: -3px"></span> Archive
                </a>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
        <template #empty>
          <img :src="emptyState.image" alt="" class="empty-state-img empty-state-img--md" />
          <h4 class="mt-3 empty-state-text">{{ emptyState.title }}</h4>
          <p class="data-table__muted">{{ emptyState.text }}</p>
        </template>
      </data-table>
    </card>

    <base-alert-dialog
      :show="dialog.show"
      :variant="dialog.variant"
      :title="dialog.title"
      :message="dialog.message"
      :show-cancel="dialog.showCancel"
      :confirm-text="dialog.confirmText"
      :confirm-variant="dialog.confirmVariant"
      @confirm="onDialogConfirm"
      @cancel="dialog.show = false"
    />
  </div>
</template>

<script>
  // Request forms list: GET /companies/{id}/groups/{grid}/request-forms returns
  // every form of the group (active and archived) in one request; the Archived
  // toggle and the name filter are applied client side.
  import { Card } from 'src/components'
  import { Auth } from 'aws-amplify'
  import { API_LOCATION, REQUEST_URL } from '../../../api-config'
  import axios from 'axios'
  import { Dropdown, DropdownItem, DropdownMenu } from 'element-ui'
  import TabRail from '@/components/TabRail.vue'
  import { tagChipStyle } from '@/util/tagChip'

  const STATUS = {
    live: { label: 'Live', color: '#00f2c3' },
    paused: { label: 'Paused', color: '#ff8d72' },
    archived: { label: 'Archived', color: '#7d7d7d' },
  }

  export default {
    name: 'RequestFormsList',
    components: {
      Card,
      TabRail,
      [Dropdown.name]: Dropdown,
      [DropdownMenu.name]: DropdownMenu,
      [DropdownItem.name]: DropdownItem,
    },
    data() {
      return {
        refreshing: false,
        didInitialFetch: false,
        forms: [],
        tabs: [{ name: 'Request forms' }],
        columns: [
          { key: 'name', label: 'Form' },
          { key: 'link', label: 'Link' },
          { key: 'events_count', label: 'Events', align: 'right' },
          { key: 'questions_count', label: 'Questions', align: 'right' },
          { key: 'pending_count', label: 'Pending requests', align: 'right' },
          { key: 'status', label: 'Status' },
        ],
        filterValues: {
          name: '',
          archived: false,
        },
        dialog: {
          show: false,
          title: '',
          message: '',
          variant: 'warning',
          showCancel: false,
          confirmText: 'OK',
          confirmVariant: 'primary',
          onConfirm: null,
          redirectHome: false,
        },
      }
    },
    computed: {
      filterSchema() {
        return [
          { key: 'name', type: 'text', label: 'Name', placeholder: 'Form name', operatorLabel: 'contains', primary: true },
          // Switch on lists archived forms instead of active ones
          { key: 'archived', type: 'toggle', label: 'Archived', primary: true },
        ]
      },
      hasActiveFilters() {
        return !!this.filterValues.name
      },
      // Archived switch: on lists archived forms, off lists active ones.
      status() {
        return this.filterValues.archived ? 'archived' : 'active'
      },
      rows() {
        const wantArchived = this.status === 'archived'
        const needle = String(this.filterValues.name || '').trim().toLowerCase()
        return this.forms.filter(f => {
          if ((Number(f.archived) === 1) !== wantArchived) return false
          if (needle && !String(f.name || '').toLowerCase().includes(needle)) return false
          return true
        })
      },
      emptyState() {
        if (this.hasActiveFilters) {
          return { image: '/img/pandas/empty-requests.png', title: 'No forms match these filters', text: 'Try a different name.' }
        }
        if (this.status === 'archived') {
          return { image: '/img/pandas/empty-requests.png', title: 'No archived forms', text: 'Forms you archive show up here.' }
        }
        return { image: '/img/pandas/empty-requests.png', title: 'No request forms yet', text: 'Create a form to collect event requests from your booking site.' }
      },
    },
    methods: {
      tagChipStyle,
      statusOf(row) {
        if (Number(row.archived) === 1) return STATUS.archived
        if (Number(row.is_active) !== 1) return STATUS.paused
        return STATUS.live
      },
      publicUrl(row) {
        const group = JSON.parse(localStorage.getItem('group') || '{}')
        return REQUEST_URL + group.code + '/' + row.slug
      },
      async copyLink(row) {
        try {
          await navigator.clipboard.writeText(this.publicUrl(row))
          this.$notify({ type: 'success', message: 'Link copied', icon: 'tim-icons icon-check-2', timeout: 2000 })
        } catch (err) {
          console.log(err)
          this.$notify({ type: 'danger', message: 'Could not copy the link', icon: 'tim-icons icon-alert-circle-exc' })
        }
      },
      // The filter bar emits one initial change after resolving saved views; that
      // is the first fetch. mounted() falls back if it never fires.
      onFiltersChanged(values) {
        this.filterValues = { ...this.filterValues, ...values, archived: values.archived === true }
        this.didInitialFetch = true
        this.fetchForms()
      },
      showDialog({ title, message, variant = 'warning', showCancel = false, confirmText = 'OK', confirmVariant = 'primary', onConfirm = null, redirectHome = false }) {
        this.dialog = { show: true, title, message, variant, showCancel, confirmText, confirmVariant, onConfirm, redirectHome }
      },
      onDialogConfirm() {
        const { onConfirm, redirectHome } = this.dialog
        this.dialog.show = false
        if (onConfirm) {
          onConfirm()
        } else if (redirectHome) {
          this.$router.push('/')
        }
      },
      errorMessage(err, fallback) {
        const data = err && err.response ? err.response.data : null
        if (data && typeof data === 'object' && data.error) return data.error
        if (typeof data === 'string') {
          try {
            const parsed = JSON.parse(data)
            if (parsed && parsed.error) return parsed.error
          } catch (e) {
            // plain string body
          }
        }
        return fallback
      },
      // 401 and 403 are permission gates on every request form endpoint; both
      // send the user home after they acknowledge. Anything else stays on the page.
      handleRequestError(err, fallbackMessage) {
        const status = err && err.response ? err.response.status : 0
        if (status === 401 || status === 403) {
          this.showDialog({ title: 'No access', message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', variant: 'danger', redirectHome: true })
          return true
        }
        if (fallbackMessage) {
          this.showDialog({ title: 'Something went wrong', message: this.errorMessage(err, fallbackMessage), variant: 'danger' })
          return true
        }
        return false
      },
      async authConfig() {
        const session = await Auth.currentSession()
        return { headers: { Authorization: session.idToken.jwtToken } }
      },
      groupUrl() {
        const companyId = localStorage.getItem('companyId')
        const companyGroupId = JSON.parse(localStorage.getItem('group')).id
        return API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId
      },
      async fetchForms() {
        this.refreshing = true
        try {
          const config = await this.authConfig()
          const response = await axios.get(this.groupUrl() + '/request-forms', config)
          this.forms = Array.isArray(response.data.request_forms) ? response.data.request_forms : []
        } catch (err) {
          console.log(err)
          this.handleRequestError(err, 'The request forms could not be loaded. Please refresh and try again.')
        } finally {
          this.refreshing = false
        }
      },
      toggleArchivePrompt(item) {
        const archiving = Number(item.archived) !== 1
        this.showDialog({
          title: 'Are you sure?',
          message: archiving
            ? 'This action will archive the form. Its public page will show the not-found page and it will no longer accept requests.'
            : 'This action will restore the form. It will accept requests again as soon as it is set to accepting requests.',
          variant: 'warning',
          showCancel: true,
          confirmText: 'Confirm',
          confirmVariant: archiving ? 'danger' : 'primary',
          onConfirm: () => this.toggleArchive(item, archiving ? 1 : 0),
        })
      },
      async toggleArchive(item, archived) {
        this.refreshing = true
        try {
          const config = await this.authConfig()
          await axios.put(this.groupUrl() + '/request-forms/' + item.id, { archived }, config)
          this.$notify({ type: 'success', message: archived ? 'Form archived' : 'Form restored', icon: 'tim-icons icon-check-2' })
          this.fetchForms()
        } catch (err) {
          this.refreshing = false
          console.log(err)
          this.handleRequestError(err, 'The form could not be updated. Please try again.')
        }
      },
      createForm() {
        this.$router.push('/bookings/request-forms/new')
      },
      editForm(row) {
        this.$router.push('/bookings/request-forms/' + row.id)
      },
      async duplicateForm(item) {
        this.refreshing = true
        try {
          const config = await this.authConfig()
          const response = await axios.post(this.groupUrl() + '/request-forms/' + item.id + '/duplicate', {}, config)
          this.refreshing = false
          this.$notify({ type: 'success', message: 'Form duplicated. You are now editing the copy.', icon: 'tim-icons icon-check-2' })
          this.$router.push('/bookings/request-forms/' + response.data.id)
        } catch (err) {
          this.refreshing = false
          console.log(err)
          this.handleRequestError(err, 'The form could not be duplicated. Please try again.')
        }
      },
    },
    mounted() {
      this.refreshing = true
      setTimeout(() => {
        if (!this.didInitialFetch) {
          this.didInitialFetch = true
          this.fetchForms()
        }
      }, 800)
    },
  }
</script>
